import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  signingMandatesApi,
  PendingMandate,
  MandateMember,
} from '../../api/signing-mandates';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';

/**
 * Checking a signing mandate against the resolution it claims to copy.
 *
 * One question: **do these say the same thing?** The names, and the
 * single-or-joint rules with their thresholds.
 *
 * ## What approving actually does
 *
 * It is the moment a board resolution starts deciding who can move that
 * company's money. Until a mandate is verified the transfer path ignores it
 * and the business runs on role permissions — which is exactly the state
 * this whole feature exists to end, where a teammate on no resolution could
 * release a payment.
 *
 * So the screen states each rule in the words the reviewer has to find in
 * the document, rather than showing raw fields. "Adaeze Chukwu — alone, any
 * amount" is something you can look for on a page; `soloLimitKobo: null` is
 * not.
 */
const cell: React.CSSProperties = {
  padding: '12px 20px',
  borderBottom: '1px solid var(--neutral-200)',
  verticalAlign: 'top',
};

const naira = (kobo: string | null) =>
  kobo === null
    ? null
    : `₦${(Number(kobo) / 100).toLocaleString('en-NG')}`;

/**
 * One member's authority, as a sentence.
 *
 * Null and zero are different clauses and have to read differently: no
 * personal cap at all, versus never on their own signature. A reviewer
 * scanning for "may sign alone" needs to see which of the two this is.
 */
function describeMember(member: MandateMember): string {
  if (member.soloLimitKobo === null) return 'alone, any amount';
  if (member.soloLimitKobo === '0') return 'never alone — always needs a second';
  return `alone, up to ${naira(member.soloLimitKobo)}`;
}

export const SigningMandateReview: React.FC = () => {
  usePage('Signing mandates');
  const toast = useToast();
  const [pending, setPending] = useState<PendingMandate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const fetchPending = async () => {
    try {
      setLoading(true);
      setError(null);
      setPending(await signingMandatesApi.pending());
    } catch {
      setError('Failed to load the mandate queue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPending();
  }, []);

  const decide = async (
    entry: PendingMandate,
    decision: 'approve' | 'reject',
  ) => {
    const note = notes[entry.mandate.id]?.trim();
    if (decision === 'reject' && !note) {
      // "Rejected" alone sends a business back to a document they believe is
      // correct, to guess at which line disagrees.
      toast.error('Say which part disagrees with the resolution.');
      return;
    }

    try {
      setBusyId(entry.mandate.id);
      await signingMandatesApi.review(entry.mandate.id, decision, note);
      toast.success(
        decision === 'approve'
          ? 'Mandate verified — it now governs who can release payments.'
          : 'Sent back to the business.',
      );
      await fetchPending();
    } catch (err) {
      toast.error(err, 'Could not record that decision.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Card padding={0}>
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--neutral-300)',
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>
            Awaiting review
          </h3>
          <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
            One question: does this say the same thing as the board resolution? Approving is
            what makes it decide who can release that company's payments.
          </p>
        </div>

        {error && (
          <div
            style={{
              margin: 20,
              padding: 12,
              background: 'var(--danger-100)',
              color: 'var(--danger-900)',
              fontSize: 13,
              borderRadius: 'var(--radius-md)',
            }}
          >
            {error}
          </div>
        )}

        {!error && pending.length === 0 && (
          <div
            style={{
              padding: 32,
              textAlign: 'center',
              color: 'var(--neutral-500)',
              fontSize: 13,
            }}
          >
            Nothing waiting.
          </div>
        )}

        {pending.map((entry) => {
          const nameOf = (memberId: string) =>
            entry.members.find((m) => m.id === memberId)?.displayName ??
            'Removed';

          return (
            <div key={entry.mandate.id} style={cell}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 16,
                  marginBottom: 12,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--neutral-800)' }}>
                    Version {entry.mandate.version}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                    Business {entry.mandate.businessId.slice(0, 8)} · submitted{' '}
                    {format(new Date(entry.mandate.createdAt), 'd MMM, HH:mm')}
                  </div>
                </div>
                {/* The document is the other half of the comparison, so the
                    way to it is on the row rather than a page away. */}
                {entry.mandate.documentId ? (
                  <a
                    href={`/business/${entry.mandate.businessId}/documents`}
                    style={{ fontSize: 13, color: 'var(--blue-600)' }}
                  >
                    {entry.resolution ? 'Open the signed resolution' : 'Open the resolution'}
                  </a>
                ) : (
                  <span style={{ fontSize: 13, color: 'var(--danger-900)' }}>
                    No resolution attached
                  </span>
                )}
              </div>

              {/* Written in PayIQ: the terms below are the resolution's own,
                  so the question is the signatures, not the wording. */}
              {entry.resolution ? (
                <div
                  style={{
                    border: '1px solid var(--neutral-300)',
                    borderRadius: 'var(--radius-md)',
                    padding: 14,
                    marginBottom: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  <div style={{ fontSize: 13, color: 'var(--neutral-800)' }}>
                    <strong>Written and signed in PayIQ.</strong> The terms below are the
                    resolution&rsquo;s own. It came here because:{' '}
                    {entry.resolution.reviewReason ?? 'no reason was recorded.'}
                  </div>
                  {entry.resolution.signatures.map((s) => (
                    <div
                      key={`${s.name}-${s.signedAt}`}
                      style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 13 }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: 'var(--neutral-800)' }}>{s.name}</div>
                        <div style={{ color: 'var(--neutral-500)', fontSize: 12 }}>
                          {s.how} · {format(new Date(s.signedAt), 'd MMM, HH:mm')}
                        </div>
                      </div>
                      {s.signatureImageUrl ? (
                        <a href={s.signatureImageUrl} target="_blank" rel="noreferrer">
                          <img
                            src={s.signatureImageUrl}
                            alt={`Signature of ${s.name}`}
                            style={{
                              maxHeight: 44,
                              maxWidth: 160,
                              border: '1px solid var(--neutral-300)',
                              borderRadius: 6,
                              background: '#fff',
                            }}
                          />
                        </a>
                      ) : null}
                      {s.uploadedCopyUrl ? (
                        <a
                          href={s.uploadedCopyUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 13, color: 'var(--blue-600)', whiteSpace: 'nowrap' }}
                        >
                          Open signed copy
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}

              <div
                style={{
                  background: 'var(--neutral-100)',
                  borderRadius: 'var(--radius-md)',
                  padding: 14,
                  fontSize: 13,
                  color: 'var(--neutral-800)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                {entry.members.map((m) => (
                  <div key={m.id}>
                    <strong>{m.displayName}</strong> — {describeMember(m)}
                    {m.subjectType === 'member' ? (
                      <span style={{ color: 'var(--neutral-500)' }}>
                        {' '}
                        (not on the share register)
                      </span>
                    ) : null}
                  </div>
                ))}

                {entry.mandate.jointThresholdKobo ? (
                  <div style={{ marginTop: 6 }}>
                    Above {naira(entry.mandate.jointThresholdKobo)}:{' '}
                    {entry.mandate.jointRule === 'any_two'
                      ? 'any two of them'
                      : 'only these pairs —'}
                    {entry.mandate.jointRule === 'named_pairs' ? (
                      <ul style={{ margin: '4px 0 0 18px' }}>
                        {entry.pairs.map((p) => (
                          <li key={p.id}>
                            {nameOf(p.firstMemberId)} and {nameOf(p.secondMemberId)}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ) : (
                  <div style={{ marginTop: 6, color: 'var(--neutral-500)' }}>
                    No amount requires a second signature.
                  </div>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  marginTop: 12,
                  alignItems: 'center',
                }}
              >
                <input
                  value={notes[entry.mandate.id] ?? ''}
                  onChange={(e) =>
                    setNotes({ ...notes, [entry.mandate.id]: e.target.value })
                  }
                  placeholder="Which part disagrees with the document?"
                  style={{
                    flex: 1,
                    height: 34,
                    padding: '0 10px',
                    fontSize: 13,
                    fontFamily: 'var(--font-family)',
                    color: 'var(--neutral-800)',
                    background: 'var(--neutral-100)',
                    border: '1px solid var(--neutral-300)',
                    borderRadius: 'var(--radius-md)',
                    outline: 'none',
                  }}
                />
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busyId === entry.mandate.id}
                  onClick={() => void decide(entry, 'reject')}
                >
                  Send back
                </Button>
                <Button
                  size="sm"
                  disabled={busyId === entry.mandate.id}
                  onClick={() => void decide(entry, 'approve')}
                >
                  Matches the resolution
                </Button>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
};
